import { DataGridColumnCurrency, ProjectIssuanceBNG } from "../../models";
import { ResultData } from "../../service/Shared";
import { DataGridColumnDefinition } from "../../widget";
import { flattenObject } from "../flattenObject";
import { jsonReviver } from "../json";

interface InventoryIssuanceLike {
  standardSpecificIssuanceData: string;
  vintageStartDate: Date | null;
  vintageEndDate: Date | null;
  [key: string]: unknown;
}

export const getInventoryColumns = (
  unitMeasurement: string,
  showPrice: boolean,
  showAvailable: boolean
): DataGridColumnDefinition[] => {
  let columns: DataGridColumnDefinition[] = [
    {
      name: "Serial Number",
      key: "serialNumber",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: false,
    },
    {
      name: "Total",
      key: "unitQuantityTotal",
      dataType: "number",
      filterable: true,
      sortable: false,
    },
  ];

  if (showAvailable) {
    columns = [
      ...columns,
      {
        name: "Available",
        key: "unitQuantityAvailable",
        dataType: "number",
        filterable: true,
        sortable: false,
      },
    ];
  }

  switch (unitMeasurement) {
    case "Carbon":
    case "Ecosystem recovery":
      columns.splice(0, 0, {
        name: "Vintage",
        key: "vintage",
        dataType: "string",
        formatter: "stringWithEllipsis",
        filterable: false,
        sortable: false,
      });
      columns.splice(5, 0, {
        name: "Unit type",
        key: "quantityType",
        dataType: "string",
        filterable: true,
        sortable: false,
      });
      break;
    case "Biodiversity":
      columns.splice(
        0,
        0,
        {
          name: "Habitat group",
          key: "habitatType",
          dataType: "string",
          formatter: "stringWithEllipsis",
          filterable: true,
          sortable: true,
        },
        {
          name: "Habitat",
          key: "habitat",
          dataType: "string",
          formatter: "stringWithEllipsis",
          filterable: true,
          sortable: true,
        },
        {
          name: "Distinctiveness",
          key: "distinctiveness",
          dataType: "string",
          formatter: "stringWithEllipsis",
          filterable: true,
          sortable: true,
        }
      );
      columns.splice(3, 0, {
        name: "Issuance date",
        key: "issuanceDate",
        dataType: "Date",
        formatter: "dateOnly",
        filterable: false,
        sortable: false,
      });
      break;
    default:
      break;
  }

  if (showPrice) {
    columns = [
      ...columns,
      {
        name: "Price",
        key: "price",
        dataType: "number",
        formatter: "currency",
        currency: DataGridColumnCurrency.Gbp,
        filterable: true,
        sortable: false,
      },
    ];
  }

  return columns;
};

export const getVintageLabel = (issuance: InventoryIssuanceLike): string => {
  return `${issuance.vintageStartDate?.getFullYear()} - ${issuance.vintageEndDate?.getFullYear()}`;
};

export const getHabitatFromStandardSpecificIssuanceData = (standardSpecificIssuanceData: string): string[] => {
  const biodiversityData = JSON.parse(standardSpecificIssuanceData, jsonReviver) as ProjectIssuanceBNG;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return [biodiversityData.habitatType!, biodiversityData.habitatDistinctiveness!];
};

export const mapInventoryIssuancesToRows = (
  issuances: InventoryIssuanceLike[],
  unitMeasurement: string
): ResultData[] => {
  return (
    issuances?.map((issuance) => {
      const row = flattenObject(issuance);

      if (unitMeasurement === "Carbon" || unitMeasurement === "Ecosystem recovery") {
        row.vintage = getVintageLabel(issuance);
      }

      return row;
    }) ?? []
  );
};
